import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import InfoOutline from "@mui/icons-material/InfoOutlined";
import ViewWeek from "@mui/icons-material/ViewWeek";
import { IconButton, Popover, Stack, Tooltip, Typography } from "@mui/material";
import { useCallback, useMemo } from "react";
import { usePopover } from "../../../../../utils/hooks/use-popover";
import { INSERT_STYLEABLE_TABLE_COMMAND } from "../../types/commands";
import split25252525 from "../table-menu/icons/split-25-25-25-25.svg";
import split255025 from "../table-menu/icons/split-25-50-25.svg";
import split2570 from "../table-menu/icons/split-25-75.svg";
import split333433 from "../table-menu/icons/split-33-34-33.svg";
import split5050 from "../table-menu/icons/split-50-50.svg";
import split7525 from "../table-menu/icons/split-75-25.svg";
import { ToolbarIconButton } from "./toolbar-icon-button";

import type { ToolbarProps } from "./props";
import type { FC } from "react";

export const ColumnsButton: FC<ToolbarProps & { label?: string }> = ({
  disabled,
  label,
  onClick,
}) => {
  const [editor] = useLexicalComposerContext();
  const [anchorRef, { handleClose, handleOpen, open }] = usePopover<HTMLButtonElement>();

  const insertTable = useCallback(
    (columnProportions: number[]) => {
      editor.dispatchCommand(INSERT_STYLEABLE_TABLE_COMMAND, {
        columnProportions,
      });

      handleClose();
      onClick?.();
    },
    [editor, onClick, handleClose],
  );

  const columnsSplits = useMemo(
    () => [
      {
        label: "2 Columns (Equal Width)",
        icon: split5050,
        onClick: () => insertTable([50, 50]),
      },
      {
        label: "2 columns (25%-75%)",
        icon: split2570,
        onClick: () => insertTable([25, 75]),
      },
      {
        label: "2 columns (75%-25%)",
        icon: split7525,
        onClick: () => insertTable([75, 25]),
      },
      {
        label: "3 Columns (Equal Width)",
        icon: split333433,
        onClick: () => insertTable([33.3, 33.4, 33.3]),
      },
      {
        label: "3 Columns (25%-50%-25%)",
        icon: split255025,
        onClick: () => insertTable([25, 50, 25]),
      },
      {
        label: "4 Columns (Equal Width)",
        icon: split25252525,
        onClick: () => insertTable([25, 25, 25, 25]),
      },
    ],
    [insertTable],
  );

  return (
    <>
      <ToolbarIconButton
        ref={anchorRef}
        data-analytics-id="lexical-toolbar-columns"
        sx={{ width: label && "100%", justifyContent: "start" }}
        disabled={disabled}
        label={label}
        icon={<ViewWeek fontSize="medium" />}
        data-testid="toolar-button-columns"
        aria-label="Columns"
        tabIndex={-1}
        onClick={() => {
          handleOpen();
        }}
      />
      <Popover
        disableAutoFocus
        open={open}
        // oxlint-disable-next-line react/refs -- MUI anchorEl needs the DOM node; the popover only opens after the anchor has mounted
        anchorEl={anchorRef.current}
        onClose={() => {
          handleClose();
        }}
        sx={{
          width: "300px",
        }}
      >
        <Stack direction="row" alignItems="center" sx={{ padding: 1 }}>
          <Typography variant="body2" sx={{ padding: 1 }}>
            PRESETS
          </Typography>
          <Tooltip
            title={<>You can change the layout after you’ve inserted it into your content.</>}
          >
            <InfoOutline />
          </Tooltip>
        </Stack>
        {columnsSplits.map((split) => (
          <IconButton
            data-analytics-id="lexical-toolbar-columns"
            key={split.label}
            onClick={() => {
              split.onClick();
              handleClose();
            }}
            sx={{
              padding: 1,
              width: "100%",
              justifyContent: "start",
            }}
          >
            <img src={split.icon} alt={split.label} />
            <Typography variant="body2" sx={{ paddingLeft: 1 }}>
              {split.label}
            </Typography>
          </IconButton>
        ))}
      </Popover>
    </>
  );
};
